import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { branded, useBrand } from '@/brand/context'
import { relativeTime } from '@/lib/format'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'

type Notification = Schema<'NotificationOut'>

/**
 * The notification centre — S-D3 screen 02, converted.
 *
 * **Why this shape** (the design's own words): notifications are the product's
 * voice when the tenant is not looking, so each one names what happened *and*
 * what it wants. Anything actionable carries its action inline, which is
 * `action_path` and `action_label` on the row — a path, never a URL, because the
 * same row is rendered into an email where the origin comes from the server.
 *
 * **Opening the panel does not mark anything read.** S-D3 draws a *Mark all
 * read* button, and having one implies the alternative is deliberate: a badge
 * that cleared itself the moment you glanced at it is a badge that loses the one
 * thing you had not dealt with. Clicking a row marks that row, because clicking
 * it is an answer.
 *
 * **The panel's primary action is the one it wants** (SHELL-21).
 * `shell.html:174` draws *Review* as `ar-btn--primary` and `:184` draws *Show
 * record* as `ar-btn--ghost` — one row per notification, at most one of them
 * asking to be pressed. Every row rendering a primary is a panel of five equal
 * demands, which is the same as none. The row that carries the tone is the
 * unread one; a row the tenant has already dealt with keeps its action and
 * loses its emphasis. Severity does not pick a button tone — see `actionTone`.
 *
 * **The badge is polled, not pushed.** No WebSocket and no SSE: a server-sent
 * stream would mean a long-lived connection per open tab against a 0.25 vCPU
 * task (COGS §8), for a count that changes a handful of times a day. Sixty
 * seconds is the same interval the dispatcher runs on, so the badge cannot be
 * more than one tick behind what has actually been sent. Polling stops while the
 * tab is hidden — `document.hidden` — because a backgrounded tab counting
 * nothing is the cheap half of this to get wrong.
 */

/** How often the unread count is refreshed while the tab is visible. */
export const POLL_INTERVAL_MS = 60_000

/** How many rows the panel shows. Enough to be a history, short enough to scan. */
const PAGE = 20

/**
 * SHELL-21. What weight a row's inline action carries.
 *
 * `primary` for anything unread and `ghost` for anything read. That is the
 * mockup's own split — its two actioned rows are one primary and one ghost —
 * generalised to a rule rather than pinned to two events, since the panel is
 * built from whatever the API returns.
 *
 * There is no `danger` tone (parity P-NOTIF-03, 2026-09-13): `shell.html`'s
 * panel draws primary and ghost buttons only. The row's severity still reaches
 * the reader through its title and body; the button says what pressing it does.
 */
function actionTone(row: Notification): 'primary' | 'ghost' {
  return row.read ? 'ghost' : 'primary'
}

export function NotificationBell(): JSX.Element {
  const navigate = useNavigate()
  const brand = useBrand()
  const [open, setOpen] = useState(false)
  const [tick, setTick] = useState(0)
  const wrapper = useRef<HTMLDivElement>(null)
  const now = new Date()

  const list = useQuery(
    (signal) =>
      call(() => api.GET('/api/v1/notifications', { params: { query: { limit: PAGE } }, signal })),
    [tick],
  )

  const markRead = useMutation((id: number) =>
    call(() =>
      api.POST('/api/v1/notifications/{notification_id}/read', {
        params: { path: { notification_id: id } },
      }),
    ),
  )
  const markAll = useMutation(() => call(() => api.POST('/api/v1/notifications/read-all', {})))

  const refresh = useCallback(() => {
    setTick((current) => current + 1)
  }, [])

  // The poll. `window.setInterval` rather than a chain of timeouts so a slow
  // response cannot make the interval drift; the visibility check is what stops
  // a backgrounded tab from counting nothing every minute for a week.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (!document.hidden) refresh()
    }, POLL_INTERVAL_MS)
    return () => {
      window.clearInterval(id)
    }
  }, [refresh])

  // Escape and click-away, exactly as the account menu does — people close a
  // panel by clicking the thing they wanted, not by clicking the button again.
  useEffect(() => {
    if (!open) return
    function onDocument(event: MouseEvent | KeyboardEvent): void {
      if (event instanceof KeyboardEvent) {
        if (event.key === 'Escape') setOpen(false)
        return
      }
      if (event.target instanceof Node && !wrapper.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocument)
    document.addEventListener('keydown', onDocument)
    return () => {
      document.removeEventListener('mousedown', onDocument)
      document.removeEventListener('keydown', onDocument)
    }
  }, [open])

  const unread = list.state.data?.unread ?? 0
  const rows = list.state.data?.notifications ?? []

  function act(row: Notification): void {
    void (async () => {
      if (!row.read) {
        const result = await markRead.run(row.id)
        if (result.ok) refresh()
      }
      if (row.action_path) {
        setOpen(false)
        void navigate(row.action_path)
      }
    })()
  }

  return (
    <div className="app-notif" ref={wrapper}>
      <button
        type="button"
        // SHELL-17. `ghost` when closed — `shell.html:103` gives the bell a
        // surface and a `--ar-border-strong` edge, where `quiet` is transparent
        // with a muted glyph. `primary` while the panel is open is kept: it is
        // what says which control the dialog belongs to.
        className={`ar-btn ar-btn--${open ? 'primary' : 'ghost'} ar-btn--sm ar-btn--icon`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={unread === 0 ? 'Notifications' : `Notifications, ${String(unread)} unread`}
        onClick={() => {
          setOpen((current) => {
            if (!current) refresh()
            return !current
          })
        }}
      >
        {/* Parity P-SHELL-05 (2026-09-13). No numeric badge: `shell.html:103`
            carries the count in the accessible name alone — *"Notifications, 3
            unread"* — and draws the bell as a plain ghost button. The number
            above is still the one a screen reader hears. */}
        <span aria-hidden="true">🔔</span>
      </button>

      {/* SHELL-36. `shell.html:214` draws the page behind the open panel at
          `opacity: .45`. A scrim rather than an opacity on the body, because
          fading the content itself would fade the text a screen magnifier is
          reading and would take the panel's own ancestors with it.

          **Portalled to `document.body`, and it has to be.** `.app-top` carries
          a `z-index` so the bar this panel hangs off stays lit — which makes it
          a stacking context, so a scrim rendered inside it would be layered
          against the bar's own children and would dim the breadcrumbs, the ☰
          and the avatar along with the page. At the root it is layered against
          the shell: above the page and the bottom bar (20), below the top bar
          (26) and therefore below the panel inside it.

          It closes on a press for the same reason the click-away handler
          exists — people close a panel by clicking the thing they wanted — and
          it needs its own handler rather than that one, because a portal stays
          inside this component's React tree: `wrapper.contains` is false for a
          node that is not in the DOM subtree, but the click-away would fire
          *and* this would, and `setOpen(false)` twice is still closed. Using
          `onMouseDown` keeps it on the same event the document listener uses so
          the two cannot disagree about which came first.

          `aria-hidden` and no role: it is scenery with a shortcut on it, and
          both routes it offers — Escape and the bell — are already reachable
          from the keyboard. */}
      {open
        ? createPortal(
            <div
              className="app-notif__scrim"
              aria-hidden="true"
              onMouseDown={() => {
                setOpen(false)
              }}
            />,
            document.body,
          )
        : null}

      {open ? (
        <div className="app-notif__panel" role="dialog" aria-label="Notifications">
          <div className="app-notif__head">
            <strong>Notifications</strong>
            {unread > 0 ? <span className="ar-badge ar-badge--warning">{unread} new</span> : null}
            <span className="ar-card__spacer" />
            {unread > 0 ? (
              <Button
                tone="quiet"
                size="sm"
                busy={markAll.pending}
                busyLabel="Marking read"
                onClick={() => {
                  void (async () => {
                    const result = await markAll.run(undefined)
                    if (result.ok) refresh()
                  })()
                }}
              >
                Mark all read
              </Button>
            ) : null}
          </div>

          <InlineError failure={markRead.state.failure ?? markAll.state.failure} />

          {list.state.status === 'loading' ? (
            <div className="app-notif__body">
              <Loading label="Loading notifications" lines={3} />
            </div>
          ) : null}

          {list.state.status === 'error' ? (
            <ErrorState
              failure={list.state.failure}
              title="Could not load notifications"
              onRetry={refresh}
            />
          ) : null}

          {list.state.status === 'ready' && rows.length === 0 ? (
            <div className="app-notif__body">
              {/* S-D3: never an empty box. */}
              <Empty
                icon="◷"
                title="You're all caught up"
                body="We'll tell you when something needs you."
              />
            </div>
          ) : null}

          {list.state.status === 'ready' && rows.length > 0 ? (
            <ul className="app-notif__list">
              {rows.map((row) => (
                <li
                  key={row.id}
                  className="app-notif__item"
                  data-unread={row.read ? undefined : ''}
                >
                  {/* SHELL-34. **The dot is the unread marker and nothing
                      else.** It used to carry an `ar-badge--*` class picked
                      from the row's severity, which the audit found had never
                      had a visible effect — `app.css` sets the dot's background
                      after the design layer sets the badge's, at equal
                      specificity, so the app layer always won. Removed rather
                      than left inert: it is a class that *sets a background* on
                      the one element whose background now carries the read /
                      unread signal, so a later reorder of the two stylesheets
                      would put a coloured dot back on every read row and undo
                      this row without touching this file. Severity still
                      decides the action's tone, which is where it is legible. */}
                  <span className="app-notif__dot" aria-hidden="true" />
                  <div className="app-notif__content">
                    <strong>{branded(row.title, brand)}</strong>
                    {row.body ? <span className="ar-muted">{branded(row.body, brand)}</span> : null}
                    {row.action_path ? (
                      <div className="app-notif__action">
                        <Button
                          tone={actionTone(row)}
                          size="sm"
                          onClick={() => {
                            act(row)
                          }}
                        >
                          {row.action_label ?? 'Open'}
                        </Button>
                      </div>
                    ) : null}
                    <time dateTime={row.created_at}>{relativeTime(row.created_at, now)}</time>
                  </div>
                  {row.read ? null : (
                    <button
                      type="button"
                      className="app-notif__read"
                      aria-label={`Mark "${row.title}" read`}
                      onClick={() => {
                        void (async () => {
                          const result = await markRead.run(row.id)
                          if (result.ok) refresh()
                        })()
                      }}
                    >
                      ✓
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : null}

          <div className="app-notif__foot">
            <Link
              to="/settings/notifications"
              onClick={() => {
                setOpen(false)
              }}
            >
              Notification settings
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  )
}
